// Cierge Labs — Cierge family registry.
//
// Single source of truth for the Cierge sub-properties surfaced from this
// codebase. Today every entry's `external_url` is null and the case-study
// card on /explore/output renders no outbound link. When a sub-property
// goes live elsewhere (e.g. CharityCierge, FreightCierge as standalone
// destinations), set its `external_url` and flip `status` to "live" —
// no other code change is required. This is the only file to touch when
// plugging in a new Cierge URL.
//
// Status values:
//   case_study_only — the case study is surfaced in the wizard output
//                     but there is no separate live destination yet
//   planned         — Cierge property exists in the family but no copy
//                     and no destination yet
//   live            — `external_url` is populated and the case-study
//                     card will render a "Visit {label}" outbound link
//
// Sector mapping note: IntelCierge → cities_resilience is a v1 placeholder.
// See /app/memory/CONTEXT.md §7. Do not silently harden the mapping.

export const CIERGE_REGISTRY = {
  healthcierge: {
    id: "healthcierge",
    label: "HealthCierge",
    sector_id: "health",
    summary:
      "Predictive cardiovascular research with patient-held data custody, purpose-bound consent, and signed receipts.",
    external_url: null,
    status: "case_study_only",
  },
  freightcierge: {
    id: "freightcierge",
    label: "FreightCierge",
    sector_id: "supply_chain",
    summary:
      "Customs assurance and supply-chain exception handling under governed requests, approvals, and handoffs.",
    external_url: null,
    status: "case_study_only",
  },
  passengercierge: {
    id: "passengercierge",
    label: "PassengerCierge",
    sector_id: "mobility",
    summary:
      "Governed passenger support across complex transport journeys with purpose-bound exchange and signed receipts.",
    external_url: null,
    status: "case_study_only",
  },
  intelcierge: {
    id: "intelcierge",
    label: "IntelCierge",
    sector_id: "cities_resilience",
    summary:
      "Governed performance reporting across complex organisations with source-verifiable metrics and accountable handoffs.",
    external_url: null,
    status: "case_study_only",
    note: "Sector mapping is a v1 placeholder — IntelCierge is broader than Cities & Resilience. See CONTEXT.md §7.",
  },
  charitycierge: {
    id: "charitycierge",
    label: "CharityCierge",
    sector_id: null,
    summary: null,
    external_url: null,
    status: "planned",
  },
};

export function lookupCiergeBySectorId(sectorId) {
  if (!sectorId) return null;
  return (
    Object.values(CIERGE_REGISTRY).find(
      (c) => c.sector_id === sectorId && c.status !== "planned",
    ) || null
  );
}

export function listLiveCierges() {
  return Object.values(CIERGE_REGISTRY).filter(
    (c) => c.status === "live" && c.external_url,
  );
}
